export const DEFAULT_FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';

export const chartGlobalOptions = {
    chart: {
        toolbar: {
            show: false,
        },
        zoom: {
            enabled: false,
        },
        fontFamily: DEFAULT_FONT_FAMILY,
        parentHeightOffset: 0,
    },
    xaxis: {
        tooltip: {
            enabled: false,
        }
    },
    title: {
        align: 'center',
        style: {
            fontSize: '12px',
        },
    },
    tooltip: {
        shared: true,
        intersect: false,
        theme: 'light',
        fillSeriesColor: false,
        arrow: false,
        style: {
            fontSize: '12px',
        }
    },
    legend: {
        position: 'top',
        clusterGroupedSeries: false,
        markers: {
            shape: 'circle',
        },
        fontSize: '12px',
    },
    plotOptions: {
        pie: {
            donut: {
                size: '50%',
            },
        }
    },
    dataLabels: {
        enabled: false,
    },
    states: {
        hover: {
            filter: {
                type: 'darken',
            },
        },
    },
}

export const areaChartOptions = {
    stroke: {
        width: 2,
    },
    fill: {
        type: 'solid',
    }
}

export const lineChartOptions = {
    markers: {
        size: 3,
        strokeWidth: 1,
        hover: {
            sizeOffset: 2,
        },
    },
    stroke: {
        curve: 'smooth',
        width: 2,
    },
}

export const rotatedXAxisLabelsOptions = {
    labels: {
        rotateAlways: true,
        rotate: -35,
        offsetX: 5,
    }
}

/**
 * @param {string} title
 * @param {{ name: string, value: number|string|null, color: string }[]} seriesData
 * @returns {HTMLDivElement}
 */
export function createChartsTooltip(title, seriesData) {
    const tooltipElement = document.createElement('div');

    tooltipElement.appendChild(createTooltipTitleElement(title));

    const nonNullSeriesData = seriesData.filter(serie => serie.value !== null);
    for (const serieData of nonNullSeriesData) {
        const seriesGroupElement = createTooltipSeriesGroupElement();

        seriesGroupElement.appendChild(createTooltipMarkerElement(serieData.color));
        seriesGroupElement.appendChild(createTooltipTextElement(serieData.name + ': ', serieData.value));

        tooltipElement.appendChild(seriesGroupElement);
    }

    return tooltipElement;
}

/**
 * @param {string} title
 * @param {{ name: string, value: number|string|null, color: string }[]} seriesData
 * @param {string} footerText
 * @returns {HTMLDivElement}
 */
export function createChartsTooltipWithFooter(title, seriesData, footerText) {
    const tooltipElement = createChartsTooltip(title, seriesData);

    const footerElement = document.createElement('div');
    footerElement.className = 'apexcharts-tooltip-footer';
    footerElement.textContent = footerText;
    tooltipElement.appendChild(footerElement);

    return tooltipElement;
}

/**
 * @param {string} title
 * @param {string} value
 * @returns {HTMLDivElement}
 */
export function createChartsTooltipWithSingleValue(title, value) {
    const tooltipElement = document.createElement('div');

    tooltipElement.appendChild(createTooltipTitleElement(title));

    const seriesGroupElement = createTooltipSeriesGroupElement();
    seriesGroupElement.appendChild(createTooltipTextElement('', value));
    tooltipElement.appendChild(seriesGroupElement);

    return tooltipElement;
}

/**
 * @param {string} title
 * @returns {HTMLDivElement}
 */
function createTooltipTitleElement(title) {
    const titleElement = document.createElement('div');
    titleElement.className = 'apexcharts-tooltip-title';
    titleElement.textContent = title;

    return titleElement;
}

/**
 * @returns {HTMLDivElement}
 */
function createTooltipSeriesGroupElement() {
    const seriesGroupElement = document.createElement('div');
    seriesGroupElement.className = 'apexcharts-tooltip-series-group apexcharts-active';

    return seriesGroupElement;
}

/**
 * @param {string} color
 * @returns {HTMLSpanElement}
 */
function createTooltipMarkerElement(color) {
    const markerElement = document.createElement('span');
    markerElement.className = 'apexcharts-tooltip-marker';
    markerElement.setAttribute('shape', 'circle');
    markerElement.style.color = color;
    markerElement.innerHTML = '<svg viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="6" cy="6" r="5" fill="currentColor"/></svg>';

    return markerElement;
}

/**
 * @param {string} label
 * @param {number|string} value
 * @returns {HTMLDivElement}
 */
function createTooltipTextElement(label, value) {
    const textElement = document.createElement('div');
    textElement.className = 'apexcharts-tooltip-text';

    const yGroupElement = document.createElement('div');
    yGroupElement.className = 'apexcharts-tooltip-y-group';

    const labelElement = document.createElement('span');
    labelElement.className = 'apexcharts-tooltip-text-y-label';
    labelElement.textContent = label;

    const valueElement = document.createElement('span');
    valueElement.className = 'apexcharts-tooltip-text-y-value';
    valueElement.textContent = value;

    yGroupElement.appendChild(labelElement);
    yGroupElement.appendChild(valueElement);
    textElement.appendChild(yGroupElement);

    return textElement;
}

/**
 * @param {HTMLElement} container
 * @param {{ name: string, color: string, inactive?: boolean }[]} series
 * @param {(serieName: string, index: number) => void} onClick
 * @param {(serieName: string) => void} onHover
 */
export function createChartsLegend(container, series, onClick, onHover) {
    const legend = document.createElement('div');
    legend.className = 'apexcharts-legend apexcharts-align-center apx-legend-position-top';

    series.forEach((serie, index) => {
        const isInactive = serie.inactive;
        const serieDiv = document.createElement('div');
        serieDiv.className = 'apexcharts-legend-series';
        serieDiv.setAttribute('data-index', index);
        serieDiv.style.margin = '4px 5px';

        const markerSpan = document.createElement('span');
        markerSpan.className = 'apexcharts-legend-marker';
        markerSpan.style.height = '16px';
        markerSpan.style.width = '16px';

        const svg = `<svg xmlns="http://www.w3.org/2000/svg" version="1.1" xmlns:xlink="http://www.w3.org/1999/xlink" width="100%" height="100%">
                                <path d="M 0, 0 m -7, 0 a 7,7 0 1,0 14,0 a 7,7 0 1,0 -14,0" fill="${serie.color}" fill-opacity="1" stroke="#ffffff" stroke-opacity="0.9" stroke-linecap="butt" stroke-width="1" stroke-dasharray="0" cx="0" cy="0" shape="circle"
                                    class="apexcharts-legend-marker apexcharts-marker apexcharts-marker-circle" style="transform: translate(50%, 50%);"></path>
                            </svg>`;
        markerSpan.innerHTML = svg;

        const textSpan = document.createElement('span');
        textSpan.className = 'apexcharts-legend-text';
        textSpan.setAttribute('data:collapsed', isInactive ? 'true' : 'false');
        textSpan.textContent = serie.name;

        serieDiv.appendChild(markerSpan);
        serieDiv.appendChild(textSpan);

        serieDiv.addEventListener('mouseover', () => {
            if (!serieDiv.classList.contains('apexcharts-inactive-legend')) onHover(serie.name);
        });

        serieDiv.addEventListener('mouseleave', () => {
            if (!serieDiv.classList.contains('apexcharts-inactive-legend')) onHover('')
        });

        serieDiv.addEventListener('click', () => {
            if (serieDiv.getAttribute('data-index') === index.toString()) {
                serieDiv.classList.toggle('apexcharts-inactive-legend');
                markerSpan.classList.toggle('apexcharts-inactive-legend');
            }

            onClick(serie.name, index)
        });

        legend.appendChild(serieDiv);
    });

    container.appendChild(legend);
}
